import { Realtime } from 'ably';
import { supabase } from '@/utils/supabase';

// ─── Ably Token Auth Client (3 separate keys) ─────────────────────────────
// SECURITY: 3 separate Ably API keys for isolation:
//   • orders  → ABLY_ORDERS_KEY  (orders:new, orders:update:*, orders:assign:*)
//   • chat    → ABLY_CHAT_KEY    (chat:order:*)
//   • staff   → ABLY_STAFF_KEY   (chat:staff, chat:staff:*)
// All tokens are issued by the ably-token edge function, scoped by user role.
// ─────────────────────────────────────────────────────────────────────────

// Per-type client state
let ordersClient: Realtime | null = null;
let chatClient: Realtime | null = null;
let staffChatClient: Realtime | null = null;

let ordersInitPromise: Promise<Realtime | null> | null = null;
let chatInitPromise: Promise<Realtime | null> | null = null;
let staffChatInitPromise: Promise<Realtime | null> | null = null;

export type AblyKeyType = 'orders' | 'chat' | 'staff';

async function fetchAblyToken(keyType: AblyKeyType): Promise<{ token: string; keyName: string; expires: number } | null> {
  try {
    const { data, error } = await supabase.functions.invoke('ably-token', {
      body: { keyType },
    });

    if (error || !data?.token) {
      if (import.meta.env.DEV) console.warn(`[Ably-${keyType}] Token fetch failed:`, error?.message || 'No token returned');
      return null;
    }

    return {
      token: data.token,
      keyName: data.keyName,
      expires: data.expires,
    };
  } catch (err: unknown) {
    if (import.meta.env.DEV) console.warn(`[Ably-${keyType}] Token fetch error:`, (err as Error).message);
    return null;
  }
}

async function getAblyClient(keyType: AblyKeyType): Promise<Realtime | null> {
  // Return existing client
  if (keyType === 'orders' && ordersClient) return ordersClient;
  if (keyType === 'chat' && chatClient) return chatClient;
  if (keyType === 'staff' && staffChatClient) return staffChatClient;

  // Return existing promise
  if (keyType === 'orders' && ordersInitPromise) return ordersInitPromise;
  if (keyType === 'chat' && chatInitPromise) return chatInitPromise;
  if (keyType === 'staff' && staffChatInitPromise) return staffChatInitPromise;

  const promise = (async () => {
    const tokenInfo = await fetchAblyToken(keyType);
    if (!tokenInfo) {
      if (keyType === 'orders') ordersInitPromise = null;
      else if (keyType === 'chat') chatInitPromise = null;
      else if (keyType === 'staff') staffChatInitPromise = null;
      return null;
    }

    const client = new Realtime({
      token: tokenInfo.token,
      authCallback: async (_tokenParams, callback) => {
        const fresh = await fetchAblyToken(keyType);
        if (fresh) {
          callback(null, fresh.token);
        } else {
          callback('Failed to refresh Ably token', null);
        }
      },
    });

    if (keyType === 'orders') ordersClient = client;
    else if (keyType === 'chat') chatClient = client;
    else if (keyType === 'staff') staffChatClient = client;

    return client;
  })();

  if (keyType === 'orders') ordersInitPromise = promise;
  else if (keyType === 'chat') chatInitPromise = promise;
  else if (keyType === 'staff') staffChatInitPromise = promise;

  return promise;
}

// ─── Legacy helpers (now async, per-key-type) ───────────────────────────

export function getOrdersAblyClient(): Promise<Realtime | null> {
  return getAblyClient('orders');
}

export function getChatAblyClient(): Promise<Realtime | null> {
  return getAblyClient('chat');
}

export function getStaffChatAblyClient(): Promise<Realtime | null> {
  return getAblyClient('staff');
}

// ─── Channel Helpers ───────────────────────────────────────────────────────

export function getOrdersChannel(client: Realtime) {
  return client.channels.get('orders:new');
}

export function getOrderUpdateChannel(client: Realtime, orderId: string) {
  return client.channels.get(`orders:update:${orderId}`);
}

export function getOrderAssignChannel(client: Realtime, staffId: string) {
  return client.channels.get(`orders:assign:${staffId}`);
}

export function getOrderChatChannel(client: Realtime, orderId: string) {
  return client.channels.get(`chat:order:${orderId}`);
}

export function getStaffChatChannel(client: Realtime) {
  return client.channels.get('chat:staff');
}

export function getStaffPrivateChatChannel(client: Realtime, staffId: string) {
  return client.channels.get(`chat:staff:${staffId}`);
}

// ─── Cleanup ─────────────────────────────────────────────────────────────

export function closeAllAblyConnections() {
  ordersClient?.close();
  ordersClient = null;
  ordersInitPromise = null;

  chatClient?.close();
  chatClient = null;
  chatInitPromise = null;

  staffChatClient?.close();
  staffChatClient = null;
  staffChatInitPromise = null;
}

// ─── Message Types ───────────────────────────────────────────────────────

export interface OrderMessage {
  id: string;
  orderId: string;
  status: string;
  assignedStaffId?: string | null;
  timestamp: number;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderRole: 'customer' | 'staff' | 'admin' | 'manager';
  senderName: string;
  text: string;
  timestamp: number;
  recipientId?: string | null;
}

export interface StaffChatMessage {
  id: string;
  senderId: string;
  senderRole: 'staff' | 'admin' | 'manager';
  senderName: string;
  text: string;
  timestamp: number;
  recipientId?: string | null; // null = broadcast to all staff
}

// ─── Health Check ────────────────────────────────────────────────────────

export function isAblyConfigured() {
  // With token auth, Ably is always available via edge function.
  // Actual connectivity is determined by getAblyClient() returning non-null.
  return true;
}

export function getAblyConfigStatus() {
  return {
    orders: !!ordersClient,
    chat: !!chatClient,
    staff: !!staffChatClient,
  };
}
